/**
 * AccountSwitcher — PULSE-47 master-account selector.
 *
 * Shows a native <select> when the current user is a master and has
 * at least one sub-account. Selecting an option updates auth-store's
 * selectedAccount, invalidates ALL data queries (so all dashboard +
 * tables re-fetch with the new scope), and persists across reloads.
 *
 * Hidden for non-master users — they're already scoped to themselves
 * by the backend.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/auth'

interface SubAccount {
  id: number
  label: string
  is_active: boolean
  status: string
}

export function AccountSwitcher() {
  const isMaster = useAuthStore((s) => s.user?.is_master ?? false)
  const selected = useAuthStore((s) => s.selectedAccount)
  const setSelected = useAuthStore((s) => s.setSelectedAccount)
  const qc = useQueryClient()

  const { data } = useQuery<SubAccount[]>({
    queryKey: ['auth', 'sub-accounts'],
    queryFn: async () => {
      const { data } = await api.get<SubAccount[]>('/auth/sub-accounts')
      return data
    },
    enabled: isMaster,
    staleTime: 5 * 60_000,
  })

  if (!isMaster || !data || data.length === 0) return null

  const onChange = (v: string) => {
    setSelected(v)
    // Invalidate everything that's account-scoped. We just nuke the
    // whole cache — cheap, and the affected hooks instantly refetch
    // with the new ?account= query param.
    qc.invalidateQueries()
  }

  return (
    <select
      value={selected}
      onChange={(e) => onChange(e.target.value)}
      className="h-7 px-2 pr-7 text-xs bg-elevated text-text border border-line
                 rounded font-mono appearance-none cursor-pointer
                 max-w-[140px] sm:max-w-none
                 hover:border-lineHi focus:outline-none focus:border-fire"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='%238893A4' d='M0 0l5 6 5-6z'/></svg>\")",
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 8px center',
      }}
      title="Account scope"
    >
      <option value="all">all · {data.length} acc</option>
      {data.map((a) => (
        <option key={a.id} value={String(a.id)}>
          {a.label} {a.is_active ? '' : '· inactive'}
        </option>
      ))}
    </select>
  )
}
